import store from '@/store'
import colors from '@/stylus/colors.json'
import types from '@/types'
import { VuetifyThemeVariant } from 'vuetify/types/services/theme'
import vuetify from '@/plugins/vuetify'
import { isValidHexColor } from '@/helpers/colorUtils'
import {
  applyThemeClass,
  applyVariableMap,
  ownDocumentThemeScopes,
  resolveVariableMap,
  ThemeDomClass,
} from '@/helpers/themeUtils'
import log from '@/log'

export const UNIFIED_PRIMARY_COLOR = '#107386'
export const UNIFIED_SECONDARY_COLOR = '#007486'
/** Classic IHM brand red (Vuetify IHMTheme.primary / iheartRadioRed). */
export const IHM_PRIMARY_COLOR = colors.IHMBranding.iheartRadioRed as string
export const UNIFIED_LOGO_URL = 'https://s3.amazonaws.com/get.unified.com/images/unified/Navy%20horizontal.png'
export const UNIFIED_ICON_URL = 'https://s3.amazonaws.com/get.unified.com/images/unified/navy-vertical-small.png'

/** Shell mount that always receives the resolved RCL theme class. */
export const THEME_SHELL_ROOT = '#ui-dashboards'

export default interface ThemeStyles {
  // The title of the app
  title: string
  light?: ThemeVariant
  dark?: ThemeVariant
}

export interface ThemeVariant {
  // The vueitfy theme
  vuetify: VuetifyThemeVariant
  // The highcharts color scheme for this theme
  highcharts: string[]
  // The loading image
  loading: string
  // The logo image
  logo: string
  // The drawer logo
  drawerLogo: string
  // True if the drawer is a dark style
  drawerDark: boolean
  // The drawer color
  drawer: string
  // The drawer accent color
  drawerAccent: string
  // The drawer selected item color
  drawerSelected: string
  // The drawer selected item accent color
  drawerSelectedAccent: string
  // The toolbar border color
  toolbarBorder: string
  // PlaceHolder
  placeHolderCompanyLogo?: string
}

export enum Theme {
  UNIFIED = 'UNIFIED',
  UNIFIED_ADMIN = 'UNIFIED_ADMIN',
  IHM = 'IHM',
}

export type ThemeApplyDiagnosis = {
  intent: 'classic-ihm' | 'white-label' | 'unified-admin' | 'unified'
  theme: Theme
  themeClass: ThemeDomClass
  missing: string[]
  shellLogoWarning: string | null
  hostname: string | undefined
  hostPrefix: string | undefined
  isIHMLocation: boolean
  isDev: boolean
  forceWhiteLabeled: boolean
  isIHMBranded: boolean
  isProvider: boolean
  customPrimary: string | null
  hasCustomTheme: boolean
  /**
   * @unified/ui-user-platform getUserTheme uses its own isIHMLocation:
   * only `hostname.split('.')[0] === 'ihm'` (isDev always false in that bundle).
   */
  userPlatformSeesIhmHost: boolean
}

/**
 * Classifies ThemeModel gates + shell (user-platform) logo mismatch for Campaign chrome.
 */
export function diagnoseThemeApply(company = types.user.getCompany()): ThemeApplyDiagnosis {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : undefined
  const hostPrefix = hostname?.split('.')[0]
  const isIHMLocation = types.config.isIHMLocation()
  const isDev = types.config.isDev()
  const forceWhiteLabeled = types.company.forceWhiteLabeled(company)
  const isIHMBranded = types.company.isIHMBranded(false, company)
  const isProvider = types.company.isProvider(company)
  const hasCustomTheme = types.company.hasCustomTheme(company)
  const customPrimary =
    company.themePrimaryColor && isValidHexColor(company.themePrimaryColor) ? company.themePrimaryColor : null

  const missing: string[] = []
  if (!isIHMLocation) missing.push('isIHMLocation=false (need ihm*/ihm-local* host or __DEV__)')
  if (forceWhiteLabeled) missing.push('forceWhiteLabeled=true (white_label_dashboard forces Theme.UNIFIED)')
  if (isProvider) missing.push('company is PROVIDER (Theme.UNIFIED_ADMIN)')
  if (isIHMLocation && !forceWhiteLabeled && !isProvider && !isIHMBranded) {
    missing.push('isIHMBranded=false (need iheart_branded on company)')
  }

  let intent: ThemeApplyDiagnosis['intent'] = 'unified'
  let theme = Theme.UNIFIED
  if (isIHMLocation && !forceWhiteLabeled && isProvider) {
    intent = 'unified-admin'
    theme = Theme.UNIFIED_ADMIN
  } else if (isIHMLocation && !forceWhiteLabeled && isIHMBranded) {
    intent = 'classic-ihm'
    theme = Theme.IHM
  } else if (forceWhiteLabeled && hasCustomTheme) {
    intent = 'white-label'
    theme = Theme.UNIFIED
  }

  // user-platform bundled ConfigModel.isIHMLocation — NOT the same as ui-dashboards
  const userPlatformSeesIhmHost = hostPrefix === 'ihm'

  let shellLogoWarning: string | null = null
  if (theme === Theme.IHM && !userPlatformSeesIhmHost) {
    shellLogoWarning =
      'Campaign shell logo comes from @unified/ui-user-platform getUserTheme, which only treats host prefix "ihm" as IHM (not ihm-local). Use e.g. ihm.cuteon.me with VUE_DEV_SERVER_HOST=ihm.cuteon.me.'
  } else if (theme !== Theme.IHM) {
    shellLogoWarning =
      'Shell will show Unified (or white-label) logo until Theme.IHM gates pass and user-platform sees host prefix ihm.'
  }

  return {
    intent,
    theme,
    themeClass: theme === Theme.IHM ? 'ihm' : 'unified',
    missing: theme === Theme.IHM ? [] : missing,
    shellLogoWarning,
    hostname,
    hostPrefix,
    isIHMLocation,
    isDev,
    forceWhiteLabeled,
    isIHMBranded,
    isProvider,
    customPrimary,
    hasCustomTheme,
    userPlatformSeesIhmHost,
  }
}

const styles: { [key in Theme]: ThemeStyles } = {
  [Theme.UNIFIED]: colors.UPTheme,
  [Theme.IHM]: colors.IHMTheme,
  [Theme.UNIFIED_ADMIN]: colors.UPAdminTheme,
}

export type ThemeVariableMap = Record<string, string>

export class ThemeModel {
  /** Campaign chrome diagnosis (gates + user-platform shell logo warning). */
  public diagnose(): ThemeApplyDiagnosis {
    return diagnoseThemeApply()
  }

  public isDark(): boolean {
    const c = this.currentStyles()
    if (store.state.dark !== null) store.state.dark
    return !c.light
  }

  /**
   * Resolves the correct ThemeVariant (light or dark) from a ThemeStyles object
   * based on the current dark mode state.
   */
  private resolveVariant(): ThemeVariant {
    const c = this.currentStyles()
    if (!c.light) return assertv(c.dark)
    if (!c.dark) return assertv(c.light)
    return this.isDark() ? assertv(c.dark) : assertv(c.light)
  }

  /**
   * Returns the active theme variant for the current user.
   * If the company has a custom white-labeled theme, applies company overrides.
   * Always syncs Vuetify so classic IHM primary (#C6002B) is applied when Theme.IHM.
   */
  public currentTheme(): ThemeVariant {
    const company = types.user.getCompany()
    const baseTheme = this.resolveVariant()

    if (types.company.forceWhiteLabeled(company) && types.company.hasCustomTheme(company)) {
      try {
        const overriddenTheme = this.applyCompanyOverrides(baseTheme)
        this.applyVuetifyTheme(overriddenTheme)
        return overriddenTheme
      } catch (e) {
        // @ts-ignore
        log.exception(e, `An error occurred when currentTheme()`)
      }
    }

    this.applyVuetifyTheme(baseTheme)
    return baseTheme
  }

  /**
   * Syncs a ThemeVariant's vuetify config into the active Vuetify theme instance.
   *
   * @param theme - The resolved ThemeVariant to apply
   */
  private applyVuetifyTheme(theme: ThemeVariant): void {
    const variant = this.isDark() ? 'dark' : 'light'
    Object.assign(vuetify.framework.theme.themes[variant], theme.vuetify)
  }

  /**
   * Merges company-specific overrides (logo, primary color, secondary color)
   * on top of the base theme variant.
   *
   * @param theme - Base ThemeVariant to extend
   * @returns New ThemeVariant with company overrides applied
   */
  private applyCompanyOverrides(theme: ThemeVariant): ThemeVariant {
    const company = types.user.getCompany()
    const themeLogoUrl = types.company.getThemeLogoUrl(company)

    const primaryColor = isValidHexColor(company.themePrimaryColor ?? '') ? company.themePrimaryColor : undefined

    const secondaryColor = isValidHexColor(company.themeSecondaryColor ?? '') ? company.themeSecondaryColor : undefined

    if (!primaryColor) log.debug('[Theme] Invalid or missing themePrimaryColor')
    if (!secondaryColor) log.debug('[Theme] Invalid or missing themeSecondaryColor')

    return {
      ...theme,
      ...(themeLogoUrl && { logo: themeLogoUrl }),
      vuetify: {
        ...theme.vuetify,
        ...(primaryColor && { primary: primaryColor }),
        ...(secondaryColor && { secondary: secondaryColor }),
      },
    }
  }

  /**
   * Maps ThemeModel.current() to an RCL theme scope class.
   * Custom white-label stays on `unified` + CSS var overrides (Campaign parity).
   */
  public resolveThemeDomClass(): ThemeDomClass {
    return this.current() === Theme.IHM ? 'ihm' : 'unified'
  }

  /**
   * Default theme roots: shell + selector matching {@link resolveThemeDomClass}.
   * IHM → `#ui-dashboards`, `.ihm`; else → `#ui-dashboards`, `.unified`.
   */
  public defaultThemeRoots(): string[] {
    return [THEME_SHELL_ROOT, `.${this.resolveThemeDomClass()}`]
  }

  /**
   * Primary chrome CSS var map from company themePrimaryColor (Campaign applyCustomTheme map).
   */
  private buildPrimaryChromeVariableMap(primaryColor: string): ThemeVariableMap {
    return {
      // Primary (CTA) button — RCL Button status="cta"
      'button-surface--cta': primaryColor,
      'button-surface--cta--hover': `${primaryColor} / 0.6`,
      'button-surface--cta--pressed': `${primaryColor} / 0.8`,

      // Secondary + tertiary (CTA) button — both read the cta--secondary tokens
      'button-text--cta--secondary': primaryColor,
      'button-border--cta--secondary': primaryColor,
      'button-surface--cta--secondary--hover': `${primaryColor} / 0.6`,
      'button-text--cta--secondary--hover': primaryColor,
      'button-border--cta--secondary--hover': primaryColor,
      'button-surface--cta--secondary--pressed': `${primaryColor} / 0.8`,
      'button-text--cta--secondary--pressed': primaryColor,
      'button-border--cta--secondary--pressed': primaryColor,

      // Do not stamp button-navigation-*: left nav must keep RCL .ihm/.unified
      // defaults (charcoal selected, grey text). Branding nav via primary reddened
      // the shell drawer when ownDocumentThemeScopes applied this map.

      // Toggle (selected state)
      'toggle-surface--selected': primaryColor,
      'toggle-border--selected': primaryColor,

      // Checkbox (selected state)
      'checkbox-surface--selected': primaryColor,
      'checkbox-border--selected': primaryColor,

      // Badge
      'badge-surface': primaryColor,

      // Tooltip
      'tooltip-surface': primaryColor,
      'tooltip-border': primaryColor,
      'tooltip-surface--hover': `${primaryColor} / 0.6`,
      'tooltip-border--hover': `${primaryColor} / 0.6`,
    }
  }

  /**
   * Stamps theme class + optional custom primary CSS vars onto one or more roots.
   * Defaults to {@link defaultThemeRoots} (IHM → `.ihm`, else → `.unified`).
   * Also owns every document theme-scope node (including portaled remounts) via
   * {@link ownDocumentThemeScopes}.
   */
  public applyResolvedTheme(roots?: string[]): void {
    const diagnosis = diagnoseThemeApply()
    const themeClass = this.resolveThemeDomClass()
    const resolvedRoots = roots ?? this.defaultThemeRoots()

    // console.warn so it shows with default console filters (Info often hidden)
    // eslint-disable-next-line no-console
    console.warn('[Theme] applyResolvedTheme', {
      ...diagnosis,
      roots: resolvedRoots,
      themeClassApplied: themeClass,
    })
    if (diagnosis.shellLogoWarning) {
      // eslint-disable-next-line no-console
      console.warn('[Theme] Campaign shell logo:', diagnosis.shellLogoWarning)
    }
    if (diagnosis.missing.length > 0) {
      // eslint-disable-next-line no-console
      console.warn('[Theme] Missing gates for classic IHM:', diagnosis.missing)
    }

    try {
      resolvedRoots.forEach((root) => applyThemeClass(root, themeClass))
      const map = this.applyCustomTheme(resolvedRoots)
      ownDocumentThemeScopes(themeClass, map ? resolveVariableMap(map) : undefined)
    } catch (e) {
      // @ts-ignore
      log.exception(e, `An error occurred when applyResolvedTheme()`)
    }
  }

  /**
   * Applies primary chrome CSS variables to the given roots (default theme-aware roots).
   * Uses company.themePrimaryColor when valid; for classic Theme.IHM with no white-label
   * primary, falls back to {@link IHM_PRIMARY_COLOR} so RCL / Violations CTAs match Vuetify red
   * (RCL `.ihm` CTA tokens are near-black by default).
   * {@link applyVariableMap} also stamps nested RCL theme scopes under each root so
   * late-mounted `.ihm` panels (Violations Export CTA) do not keep near-black defaults.
   * Does not set theme class — use {@link applyResolvedTheme} for class + vars.
   *
   * @param roots - CSS selectors to stamp (MutationObserver fallback per root)
   * @returns Raw hex ThemeVariableMap when applied, otherwise `null`
   */
  public applyCustomTheme(roots?: string[]): ThemeVariableMap | null {
    const resolvedRoots = roots ?? this.defaultThemeRoots()
    const company = types.user.getCompany()
    const companyPrimary =
      company.themePrimaryColor && isValidHexColor(company.themePrimaryColor) ? company.themePrimaryColor : null

    let primaryColor = companyPrimary
    let primarySource: 'company' | 'ihm-fallback' = 'company'

    if (!primaryColor) {
      if (this.current() === Theme.IHM) {
        primaryColor = IHM_PRIMARY_COLOR
        primarySource = 'ihm-fallback'
      } else {
        // eslint-disable-next-line no-console
        console.warn('[Theme] applyCustomTheme skipped — no valid themePrimaryColor', { roots: resolvedRoots })
        return null
      }
    }

    try {
      // eslint-disable-next-line no-console
      console.warn('[Theme] applyCustomTheme', { roots: resolvedRoots, primaryColor, primarySource })
      const map = this.buildPrimaryChromeVariableMap(primaryColor)
      resolvedRoots.forEach((root) => applyVariableMap(root, map))
      return map
    } catch (e) {
      // @ts-ignore
      log.exception(e, `An error occurred when applyCustomTheme()`)
      return null
    }
  }

  public currentStyles(): ThemeStyles {
    const theme = this.current()
    return styles[theme]
  }
  public current(): Theme {
    const c = types.company.currentLoggedIn()

    // Rig To Have an IHM theme By Default
    if (!types.config.isIHMLocation()) {
      return Theme.UNIFIED
    }

    if (types.company.forceWhiteLabeled(c)) {
      return Theme.UNIFIED
    }

    if (types.company.isProvider(c)) {
      return Theme.UNIFIED_ADMIN
    }

    if (types.company.isIHMBranded(false, c)) {
      return Theme.IHM
    }

    return Theme.UNIFIED
  }
}
